import type { UserGroupStudentDto, UserGroupWithStudentsDto } from "../../models"
import { getGroupsWithStudents, shareWithGroupsOrStudents } from "../../services/UserService"
import type { AppThunkAction } from "../app.types"
import { fetchBosatlas } from "../bosatlas/bosatlas.actions"
import type { Comparison } from "../comparisons/comparison.types"
import type { Project } from "../projects/projects.types"
import type { Group, Student } from "../user/user.types"
import type { AppThunkDispatch } from "./../app.types"
import type {
  MultiShareActions,
  MultiShareComparisonAction,
  MultiShareGroupAction,
  MultiShareGroupListAction,
  MultiShareProjectAction
} from "./multi-share.types"

export const MULTI_SHARE_OPEN_DIALOG = "MULTI_SHARE_OPEN_DIALOG"
export const MULTI_SHARE_CLOSE_DIALOG = "MULTI_SHARE_CLOSE_DIALOG"

export const MULTI_SHARE_SELECT_PROJECT = "MULTI_SHARE_SELECT_PROJECT"
export const MULTI_SHARE_DESELECT_PROJECT = "MULTI_SHARE_DESELECT_PROJECT"

export const MULTI_SHARE_SELECT_COMPARISON = "MULTI_SHARE_SELECT_COMPARISON"
export const MULTI_SHARE_DESELECT_COMPARISON = "MULTI_SHARE_DESELECT_COMPARISON"

export const MULTI_SHARE_SELECT_GROUP_ALL = "MULTI_SHARE_SELECT_GROUP_ALL"
export const MULTI_SHARE_DESELECT_GROUP_ALL = "MULTI_SHARE_DESELECT_GROUP_ALL"
export const MULTI_SHARE_SELECT_GROUP = "MULTI_SHARE_SELECT_GROUP"
export const MULTI_SHARE_DESELECT_GROUP = "MULTI_SHARE_DESELECT_GROUP"

export const MULTI_SHARE_SELECT_STUDENT = "MULTI_SHARE_SELECT_STUDENT"
export const MULTI_SHARE_DESELECT_STUDENT = "MULTI_SHARE_DESELECT_STUDENT"

export const MULTI_SHARE_FETCH_GROUPS = "MULTI_SHARE_FETCH_GROUPS"
export const MULTI_SHARE_LOAD_GROUPS = "MULTI_SHARE_LOAD_GROUPS"

export const MULTI_SHARE_SHARE = "MULTI_SHARE_SHARE"

export function multiShareOpenDialog(): MultiShareActions {
  return { type: MULTI_SHARE_OPEN_DIALOG }
}

export function multiShareCloseDialog(): MultiShareActions {
  return { type: MULTI_SHARE_CLOSE_DIALOG }
}

export function multiShareSelectProject(project: Project): MultiShareProjectAction {
  return { type: MULTI_SHARE_SELECT_PROJECT, payload: project }
}

export function multiShareDeselectProject(project: Project): MultiShareProjectAction {
  return { type: MULTI_SHARE_DESELECT_PROJECT, payload: project }
}

export function multiShareSelectComparison(comparison: Comparison): MultiShareComparisonAction {
  return { type: MULTI_SHARE_SELECT_COMPARISON, payload: comparison }
}

export function multiShareDeselectComparison(comparison: Comparison): MultiShareComparisonAction {
  return { type: MULTI_SHARE_DESELECT_COMPARISON, payload: comparison }
}

export function multiShareSelectAllGroups(groups: Group[]): MultiShareGroupListAction {
  return {
    type: MULTI_SHARE_SELECT_GROUP_ALL,
    payload: groups
  }
}

export function multiShareDeselectAllGroups(): MultiShareActions {
  return {
    type: MULTI_SHARE_DESELECT_GROUP_ALL
  }
}

export function multiShareSelectGroup(group: Group): MultiShareGroupAction {
  return {
    type: MULTI_SHARE_SELECT_GROUP,
    payload: group
  }
}

export function multiShareDeselectGroup(group: Group): MultiShareGroupAction {
  return {
    type: MULTI_SHARE_DESELECT_GROUP,
    payload: group
  }
}

export function multiShareSelectStudent(group: Group, student: Student): MultiShareGroupAction {
  const studentGroup: Group = {
    id: group.id,
    name: group.name,
    students: [student],
    isClass: group.isClass
  }
  return {
    type: MULTI_SHARE_SELECT_STUDENT,
    payload: studentGroup
  }
}

export function multiShareDeselectStudent(group: Group, student: Student): MultiShareGroupAction {
  const studentGroup: Group = {
    id: group.id,
    name: group.name,
    students: [student],
    isClass: group.isClass
  }
  return {
    type: MULTI_SHARE_DESELECT_STUDENT,
    payload: studentGroup
  }
}

export function multiShareFetchGroups(): AppThunkAction<void, MultiShareGroupListAction> {
  return async (dispatch: AppThunkDispatch<MultiShareGroupListAction>) => {
    dispatch({ type: MULTI_SHARE_FETCH_GROUPS })
    const groups: UserGroupWithStudentsDto[] = await getGroupsWithStudents()

    dispatch({
      type: MULTI_SHARE_LOAD_GROUPS,
      payload: groups.map((dto: UserGroupWithStudentsDto) => {
        return groupWithStudentDtoToGroups(dto)
      })
    })
  }
}

export function multiShareConfirm(
  groups: Group[],
  students: Student[],
  projects: Project[],
  comparisons: Comparison[]
): AppThunkAction<void, MultiShareActions> {
  return async (dispatch: AppThunkDispatch<MultiShareActions>) => {
    dispatch({ type: MULTI_SHARE_SHARE })
    await shareWithGroupsOrStudents(
      groups.map((group: Group) => group.id),
      students.map((student: Student) => student.id),
      projects.map((project: Project) => project.id as string),
      comparisons.map((comparison: Comparison) => comparison.id as string)
    )

    dispatch(fetchBosatlas())
  }
}

function groupWithStudentDtoToGroups(dto: UserGroupWithStudentsDto): Group {
  return {
    id: dto.id,
    name: dto.name as string,
    students: dto.students
      ? dto.students.map((student: UserGroupStudentDto) => {
          return {
            id: student.id,
            name: `${student.firstName} ${student.lastName}`
          }
        })
      : []
  }
}
